import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { ComplaintFormValues } from "./types";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { supportLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";
import { followUpOptionLabel } from "@/utils/complaintFollowUpLabels";
import {
  BODY_PARTS,
  BODY_PART_DAMAGE_TYPES,
  BRAKE_DAMAGE_TYPES,
  BRAKE_POSITIONS,
  ENGINE_COMPONENTS,
  ENGINE_PROBLEMS,
  INTERIOR_DAMAGE_TYPES,
  INTERIOR_LOCATIONS,
  INTERIOR_SECTIONS,
  TIRE_AND_RIM_DAMAGE_TYPES,
  TIRE_POSITIONS,
  UNDERBODY_DAMAGE_TYPES,
  UNDERBODY_PARTS,
  WHEEL_TYPES,
} from "./complaintFollowUpCatalog";

interface ComplaintFollowUpFieldsProps {
  form: UseFormReturn<ComplaintFormValues>;
  categoryId: string;
}

function FollowUpSelect({
  form,
  name,
  label,
  placeholder,
  options,
  onValueChange,
}: {
  form: UseFormReturn<ComplaintFormValues>;
  name: keyof ComplaintFormValues;
  label: string;
  placeholder: string;
  options: { id: string; label: string }[];
  onValueChange?: (value: string) => void;
}) {
  const { baseText, isDark } = useTextColorClass();

  return (
    <FormField
      control={form.control}
      name={name}
      rules={{ required: true }}
      render={({ field }) => (
        <FormItem>
          <FormLabel className={`${baseText} text-sm`}>{label}</FormLabel>
          <Select
            onValueChange={(value) => {
              field.onChange(value);
              onValueChange?.(value);
            }}
            value={typeof field.value === "string" && field.value ? field.value : undefined}
          >
            <FormControl>
              <SelectTrigger
                className={
                  isDark
                    ? "bg-black/20 border-white/20 text-white"
                    : "bg-white border-gray-200 text-black"
                }
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent
              className={
                isDark
                  ? "bg-zinc-900 border-white/10 max-h-[240px]"
                  : "bg-white border-gray-200 max-h-[240px]"
              }
            >
              {options.map((option) => (
                <SelectItem
                  key={option.id}
                  value={option.id}
                  className={
                    isDark
                      ? "text-white hover:bg-zinc-800"
                      : "text-black hover:bg-gray-100"
                  }
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormItem>
      )}
    />
  );
}

export const ComplaintFollowUpFields = ({
  form,
  categoryId,
}: ComplaintFollowUpFieldsProps) => {
  const lang = getLanguage();
  const labels = supportLabels(lang);
  const location = form.watch("damageLocation");
  const engineComponent = form.watch("engineComponent");
  const interiorSection = form.watch("interiorSection");
  const wheelType = form.watch("wheelType");

  const option = (category: string, kind: string, id: string) => ({
    id,
    label: followUpOptionLabel(category, kind, id, lang),
  });

  if (categoryId === "body") {
    const q = labels.bodyDamage ?? {};
    return (
      <div className="space-y-3">
        <FollowUpSelect
          form={form}
          name="damageLocation"
          label={q.whereIsDamage ?? "Var på karossen finns skadan?"}
          placeholder={q.selectPlacement ?? "Välj placering"}
          options={BODY_PARTS.map((id) => option("body", "parts", id))}
          onValueChange={() => form.setValue("damageType", undefined)}
        />
        {location && (
          <FollowUpSelect
            form={form}
            name="damageType"
            label={q.whatDamageType ?? "Vilken typ av skada är det?"}
            placeholder={q.selectDamageType ?? "Välj skadetyp"}
            options={(BODY_PART_DAMAGE_TYPES[location] || []).map((id) =>
              option("body", "damageTypes", id),
            )}
          />
        )}
      </div>
    );
  }

  if (categoryId === "engine") {
    const q = labels.engine ?? {};
    return (
      <div className="space-y-3">
        <FollowUpSelect
          form={form}
          name="engineComponent"
          label={q.whichComponent ?? "Vilken motorkomponent gäller det?"}
          placeholder={q.selectComponent ?? "Välj komponent"}
          options={ENGINE_COMPONENTS.map((id) =>
            option("engine", "components", id),
          )}
          onValueChange={() => form.setValue("damageType", undefined)}
        />
        {engineComponent && (
          <FollowUpSelect
            form={form}
            name="damageType"
            label={q.whatProblemType ?? "Vilken typ av problem är det?"}
            placeholder={q.selectProblemType ?? "Välj problemtyp"}
            options={(ENGINE_PROBLEMS[engineComponent] || []).map((id) =>
              option("engine", "problems", id),
            )}
          />
        )}
      </div>
    );
  }

  if (categoryId === "interior") {
    const q = labels.interior ?? {};
    return (
      <div className="space-y-3">
        <FollowUpSelect
          form={form}
          name="interiorSection"
          label={q.whichPart ?? "Vilken del av interiören?"}
          placeholder={q.selectPart ?? "Välj del av interiören"}
          options={INTERIOR_SECTIONS.map((id) =>
            option("interior", "sections", id),
          )}
          onValueChange={() => {
            form.setValue("damageLocation", undefined);
            form.setValue("damageType", undefined);
          }}
        />
        {interiorSection && (
          <FollowUpSelect
            form={form}
            name="damageLocation"
            label={q.specifyLocation ?? "Specificera plats"}
            placeholder={q.selectSpecificLocation ?? "Välj specifik plats"}
            options={(INTERIOR_LOCATIONS[interiorSection] || []).map((id) =>
              option("interior", "locations", id),
            )}
            onValueChange={() => form.setValue("damageType", undefined)}
          />
        )}
        {location && (
          <FollowUpSelect
            form={form}
            name="damageType"
            label={q.whatDamageType ?? "Vilken typ av skada är det?"}
            placeholder={q.selectDamageType ?? "Välj skadetyp"}
            options={(INTERIOR_DAMAGE_TYPES[location] || []).map((id) =>
              option("interior", "damageTypes", id),
            )}
          />
        )}
      </div>
    );
  }

  if (categoryId === "underbody") {
    const q = labels.underbody ?? {};
    return (
      <div className="space-y-3">
        <FollowUpSelect
          form={form}
          name="damageLocation"
          label={q.whichPart ?? "Vilken del av underredet gäller det?"}
          placeholder={q.selectComponent ?? "Välj komponent"}
          options={UNDERBODY_PARTS.map((id) => option("underbody", "parts", id))}
          onValueChange={() => form.setValue("damageType", undefined)}
        />
        {location && (
          <FollowUpSelect
            form={form}
            name="damageType"
            label={q.whatDamageType ?? "Vilken typ av skada är det?"}
            placeholder={q.selectDamageType ?? "Välj skadetyp"}
            options={(UNDERBODY_DAMAGE_TYPES[location] || []).map((id) =>
              option("underbody", "damageTypes", id),
            )}
          />
        )}
      </div>
    );
  }

  if (categoryId === "tires") {
    const q = labels.tires ?? {};
    return (
      <div className="space-y-3">
        <FollowUpSelect
          form={form}
          name="wheelType"
          label={q.whichWheelType ?? "Vilken typ av hjul gäller det?"}
          placeholder={q.selectWheelType ?? "Välj hjultyp"}
          options={WHEEL_TYPES.map((id) => option("tires", "wheelTypes", id))}
          onValueChange={() => {
            form.setValue("damageLocation", undefined);
            form.setValue("damageType", undefined);
          }}
        />
        {wheelType && (
          <FollowUpSelect
            form={form}
            name="damageLocation"
            label={q.whichWheel ?? "Vilket hjul gäller det?"}
            placeholder={q.selectPosition ?? "Välj position"}
            options={TIRE_POSITIONS.map((id) =>
              option("tires", "positions", id),
            )}
            onValueChange={() => form.setValue("damageType", undefined)}
          />
        )}
        {location && (
          <FollowUpSelect
            form={form}
            name="damageType"
            label={q.whatDamageType ?? "Vilken typ av skada är det?"}
            placeholder={q.selectDamageType ?? "Välj skadetyp"}
            options={TIRE_AND_RIM_DAMAGE_TYPES.map((id) =>
              option("tires", "damageTypes", id),
            )}
          />
        )}
      </div>
    );
  }

  if (categoryId === "brakes") {
    const q = labels.brakes ?? {};
    return (
      <div className="space-y-3">
        <FollowUpSelect
          form={form}
          name="damageLocation"
          label={q.whichBrake ?? "Vilken broms gäller det?"}
          placeholder={q.selectPosition ?? "Välj position"}
          options={BRAKE_POSITIONS.map((id) =>
            option("brakes", "positions", id),
          )}
          onValueChange={() => form.setValue("damageType", undefined)}
        />
        {location && (
          <FollowUpSelect
            form={form}
            name="damageType"
            label={q.whatDamageType ?? "Vilken typ av skada är det?"}
            placeholder={q.selectDamageType ?? "Välj skadetyp"}
            options={BRAKE_DAMAGE_TYPES.map((id) =>
              option("brakes", "damageTypes", id),
            )}
          />
        )}
      </div>
    );
  }

  return null;
};
